import { useCallback, useEffect, useState, VFC } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import jwtDecode from 'jwt-decode'

import { loginSucceeded } from 'authentication/Login/ducks/authentication'

import * as api from 'authentication/shared/api'
import { useTranslation } from 'react-i18n-lite'
import { AuthenticationType } from 'authentication/shared/types'
import { isEmbedded } from 'shared/utils/iframe'

const SYNCHRONIZATION_ERROR_CORRECTION = 300
const SHOW_ALERT_FROM = 300

const AuthenticationRenewal: VFC = () => {
  const [timeLeft, setTimeLeft] = useState(0)
  const [hasStickyAlert, setHasStickyAlert] = useState(false)

  const { expiration_date: expirationDate, token } = useSelector(
    (state: AuthenticationType) => state?.authentication
  )
  const dispatch = useDispatch()

  const handleScroll = useCallback(() => {
    const offset = window.scrollY
    let isSticky = false

    if (offset > 100) {
      isSticky = true
    }

    setHasStickyAlert(isSticky)
  }, [])

  const updateTimeLeft = useCallback(() => {
    const now = Math.round(Date.now() / 1000)
    const newTimeLeft = expirationDate - now - SYNCHRONIZATION_ERROR_CORRECTION
    setTimeLeft(newTimeLeft)
  }, [expirationDate])

  const logoutOnExpire = () => {
    if (timeLeft < 0) {
      dispatch({ type: 'login/LOGOUT' })
    }
  }

  const handleTokenReload = () => {
    api
      .postReloadToken({ token })
      .then((response: any) => {
        if (response && response.success && response.data) {
          const decoded = jwtDecode<{ email: string }>(token)
          dispatch(
            loginSucceeded(decoded.email, {
              token: response.data.token,
              expiration_date: response.data.expiration_date
            })
          )
        }
      })
      .catch((error: any) => {
        console.log(error.message)
      })
  }

  useEffect(() => {
    document.addEventListener('scroll', handleScroll)

    return () => {
      document.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  useEffect(() => {
    const interval = setInterval(updateTimeLeft, 1000)

    return () => {
      clearInterval(interval)
    }
  }, [updateTimeLeft])

  useEffect(logoutOnExpire, [timeLeft, dispatch])

  const { t } = useTranslation()

  /* 5 minutes less to avoid clock synchronization problems */
  if (!isEmbedded() && timeLeft > 0 && timeLeft < SHOW_ALERT_FROM) {
    return (
      <div
        className={`${hasStickyAlert ? 'sticky-alert' : ''}`}
        style={{
          width: '100%',
          background: '#a31a1a',
          zIndex: 1000
        }}
      >
        <div
          style={{ padding: '10px' }}
          className='text-white d-flex justify-content-center align-items-center'
        >
          {t('auth-renewal.session-ends', { timeLeft: timeLeft.toString() })}

          <button
            type='button'
            className='btn btn-light btn-sm ml-3'
            onClick={handleTokenReload}
          >
            {t('auth-renewal.reload-session')}
          </button>
        </div>
      </div>
    )
  } else {
    return null
  }
}

export default AuthenticationRenewal
